import { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { MainLayout } from '../components/layout';
import { RoleGuard } from '../components/auth/RoleGuard';
import { ResourceRedirect } from '../components/auth/ResourceRedirect';

// Loading fallback
function PageLoader() {
  return (
    <div
      className="flex items-center justify-center min-h-[300px]"
      style={{ background: 'var(--surface-base)' }}
    >
      <div
        className="w-6 h-6 rounded-full border-2 animate-spin"
        style={{ borderColor: 'var(--border-strong)', borderTopColor: 'var(--accent-primary)' }}
      />
    </div>
  );
}

// Lazy-loaded pages
const LoginPage = lazy(() => import('../pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const AuthCallbackPage = lazy(() => import('../pages/AuthCallbackPage').then((m) => ({ default: m.AuthCallbackPage })));
const DashboardPage = lazy(() => import('../pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const ProjectListPage = lazy(() => import('../pages/ProjectListPage').then((m) => ({ default: m.ProjectListPage })));
const ProjectDetailPage = lazy(() => import('../pages/ProjectDetailPage').then((m) => ({ default: m.ProjectDetailPage })));
const ProjectCreatePage = lazy(() => import('../pages/ProjectCreatePage').then((m) => ({ default: m.ProjectCreatePage })));
const EpisodeDetailPage = lazy(() => import('../pages/EpisodeDetailPage').then((m) => ({ default: m.EpisodeDetailPage })));
const MyTasksPage = lazy(() => import('../pages/MyTasksPage').then((m) => ({ default: m.MyTasksPage })));
const DesignReviewPage = lazy(() => import('../pages/DesignReviewPage').then((m) => ({ default: m.DesignReviewPage })));
const DesignReviewProjectPage = lazy(() => import('../pages/DesignReviewProjectPage').then((m) => ({ default: m.DesignReviewProjectPage })));
const DesignReviewViewerPage = lazy(() => import('../pages/DesignReviewViewerPage').then((m) => ({ default: m.DesignReviewViewerPage })));
const ResourceManagement = lazy(() => import('../pages/resources/ResourceManagement').then((m) => ({ default: m.ResourceManagement })));
const ClientManagement = lazy(() => import('../pages/clients/ClientManagement').then((m) => ({ default: m.ClientManagement })));
const DesignTeamManagement = lazy(() => import('../pages/design-teams/DesignTeamManagement').then((m) => ({ default: m.DesignTeamManagement })));
const LanguageTeamManagement = lazy(() => import('../pages/language-teams/LanguageTeamManagement').then((m) => ({ default: m.LanguageTeamManagement })));
const UserManagement = lazy(() => import('../pages/users/UserManagement').then((m) => ({ default: m.UserManagement })));
const ContentsPage = lazy(() => import('../pages/ContentsPage').then((m) => ({ default: m.ContentsPage })));
const TeamAssignmentRules = lazy(() => import('../pages/help/TeamAssignmentRules').then((m) => ({ default: m.TeamAssignmentRules })));

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <S><LoginPage /></S>,
  },
  {
    path: '/auth/callback',
    element: <S><AuthCallbackPage /></S>,
  },
  {
    path: '/',
    element: <MainLayout />,
    children: [
      // RESOURCE → /my-tasks, others → /dashboard
      { index: true, element: <ResourceRedirect /> },
      {
        path: 'dashboard',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM', 'DESIGN_TEAM']}>
            <S><DashboardPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'projects',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM', 'DESIGN_TEAM']}>
            <S><ProjectListPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'projects/new',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER']}>
            <S><ProjectCreatePage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'projects/:projectId',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM', 'DESIGN_TEAM']}>
            <S><ProjectDetailPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'projects/:projectId/episodes/:episodeId',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM', 'DESIGN_TEAM']}>
            <S><EpisodeDetailPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'my-tasks',
        element: <S><MyTasksPage /></S>,
      },
      {
        path: 'design-review',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'DESIGN_TEAM']}>
            <S><DesignReviewPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'design-review/:projectId',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'DESIGN_TEAM', 'EDITOR', 'TRANSLATOR', 'DESIGNER']}>
            <S><DesignReviewProjectPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'design-review/:projectId/episodes/:episodeId/:lang',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'DESIGN_TEAM', 'EDITOR', 'TRANSLATOR', 'DESIGNER']}>
            <S><DesignReviewViewerPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'resources',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM', 'DESIGN_TEAM']}>
            <S><ResourceManagement /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'clients',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER']}>
            <S><ClientManagement /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'design-teams',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'DESIGN_TEAM']}>
            <S><DesignTeamManagement /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'language-teams',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER', 'LANGUAGE_TEAM']}>
            <S><LanguageTeamManagement /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'contents',
        element: (
          <RoleGuard requiredRole={['SUPER_ADMIN', 'ADMIN', 'PROJECT_MANAGER']}>
            <S><ContentsPage /></S>
          </RoleGuard>
        ),
      },
      {
        path: 'users',
        element: (
          <RoleGuard requiredRole="SUPER_ADMIN">
            <S><UserManagement /></S>
          </RoleGuard>
        ),
      },
      {
        // No RoleGuard — anyone with an account can hit this URL via the
        // tooltip "Why is this disabled?" link. Sidebar nav is gated to
        // PM/Admin separately in navigation.ts.
        path: 'help/team-assignment-rules',
        element: <S><TeamAssignmentRules /></S>,
      },
    ],
  },
  {
    path: '*',
    element: <ResourceRedirect />,
  },
]);
